// TODO - Type 'any' needs to be fixed.
import { Box, Switch, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers-pro-6";
import { AdapterDayjs } from "@mui/x-date-pickers-pro-6/AdapterDayjs";
import { format, isFuture } from "date-fns";
import dayjs from "dayjs";
import React from "react";
import { useTranslation } from "react-i18next";

import FileUpload from "./file-upload";

type ValidityPeriodUnit = "d" | "m" | "y";

// dayjs manipulation unit identifiers (NOT user-facing) — leave in English
const validityPeriodUnits: Record<ValidityPeriodUnit, any> = {
	d: "day",
	m: "month",
	y: "year",
};

const formatDate = (value: any) => {
	return format(new Date(value.$d), "yyyy-MM-dd");
};

const EditCertificationForm = ({
	values,
	setFieldValue,
	files,
	setFiles,
	setFilesToDelete,
	fileUploadErrors,
	errors,
	validateForm,
}: any) => {
	const { t } = useTranslation();

	// user-facing translated unit words for the validity-period display
	const validityPeriodUnitLabels: Record<ValidityPeriodUnit, string> = {
		d: t("workers_day"),
		m: t("workers_month"),
		y: t("workers_year"),
	};

	const handleValidFromChange = async(newValue: any) => {
		if(!newValue) {
			setFieldValue("validFrom", null);
			if(values.validityPeriod && values.validityPeriodUnit) {
				setFieldValue("validTo", null);
				await validateForm();
			}
			return;
		}

		if (!dayjs(newValue).isValid()) {
			return;
		}

		const formattedDate = formatDate(newValue);
		setFieldValue("validFrom", formattedDate);

		if (values.validityPeriod && values.validityPeriodUnit) {
			const unit = values.validityPeriodUnit as ValidityPeriodUnit;
			if (unit in validityPeriodUnits) {
				const validToDate = dayjs(newValue).add(
					values.validityPeriod,
					validityPeriodUnits[unit]
				);
				setFieldValue("validTo", formatDate(validToDate));
			}
			await validateForm();
		}
	};

	const handleValidToChange = (newValue: any) => {
		if(!newValue) {
			setFieldValue("validTo", null);
			return;
		}
		if (!dayjs(newValue).isValid()) {
			return;
		}
		const formattedDate = format(new Date(newValue.$d), "yyyy-MM-dd");
		setFieldValue("validTo", formattedDate);
	};

	const getStatus = () => {
		if (!values.validFrom || isFuture(new Date(values.validFrom))) {
			return "-";
		} else if (!values.validTo || isFuture(new Date(values.validTo))) {
			return t("workers_active");
		} else {
			return t("workers_expired");
		}
	};

	return (
		<Box>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 1,
					mb: 1,
				}}
			>
				<Box>
					<Typography
						sx={{
							fontFamily: "Roboto",
							fontSize: "12px",
							fontWeight: 400,
							lineHeight: "12px",
							letterSpacing: "0.15px",
							color: "#00000099",
						}}
					>
						{t("workers_certification-name")}
					</Typography>
					<Typography
						sx={{
							fontFamily: "Roboto",
							fontSize: "16px",
							fontWeight: 400,
							lineHeight: "28px",
							letterSpacing: "0.15px",
							pl: 0.2,
						}}
					>
						{values.certName}
					</Typography>
				</Box>
				<Box>
					<Typography
						sx={{
							fontFamily: "Roboto",
							fontSize: "12px",
							fontWeight: 400,
							lineHeight: "12px",
							letterSpacing: "0.15px",
							color: "#00000099",
						}}
					>
						{t("workers_status")}
					</Typography>
					<Typography
						sx={{
							fontFamily: "Roboto",
							fontSize: "16px",
							fontWeight: 400,
							lineHeight: "28px",
							letterSpacing: "0.15px",
							pl: 0.2,
						}}
					>
						{getStatus()}
					</Typography>
				</Box>
				<Box>
					<Typography
						sx={{
							fontFamily: "Roboto",
							fontSize: "12px",
							fontWeight: 400,
							lineHeight: "12px",
							letterSpacing: "0.15px",
							color: "#00000099",
						}}
					>
						{t("workers_validity-period")}
					</Typography>
					<Typography
						sx={{
							fontFamily: "Roboto",
							fontSize: "16px",
							fontWeight: 400,
							lineHeight: "28px",
							letterSpacing: "0.15px",
							pl: 0.2,
						}}
					>
						{values.validityPeriod && values.validityPeriodUnit
							? t("workers_valid-for-period", {
								count: values.validityPeriod,
								unit: validityPeriodUnitLabels[
									values.validityPeriodUnit as ValidityPeriodUnit
								],
							})
							: "-"}
					</Typography>
				</Box>
			</Box>
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: "repeat(2, 1fr)",
					gap: 2,
					mb: 3,
				}}
			>
				<Box>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							value={
								values.validFrom ? dayjs(values.validFrom, "YYYY-MM-DD") : null
							}
							onChange={(newValue) => {
								handleValidFromChange(newValue);
							}}
							label={t("workers_valid-from")}
							slotProps={{
								field: { clearable: true },
								textField: {
									placeholder: t("workers_select-date"),
									variant: "filled",
									helperText: errors.validFrom,
									error: !!errors.validFrom,
								},
							}}
							format={"MM/DD/YYYY"}
							sx={{ width: "247px" }}
						/>
					</LocalizationProvider>
				</Box>
				<Box>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							value={
								values.validTo ? dayjs(values.validTo, "YYYY-MM-DD") : null
							}
							onChange={(newValue) => {
								handleValidToChange(newValue);
							}}
							label={t("workers_valid-until")}
							slotProps={{
								field: { clearable: true },
								textField: {
									placeholder: t("workers_select-date"),
									variant: "filled",
									helperText: errors.validTo,
									error: !!errors.validTo,
								},
							}}
							format={"MM/DD/YYYY"}
							sx={{ width: "247px" }}
							disabled={
								values.validityPeriod && values.validityPeriodUnit
									? true
									: false
							}
						/>
					</LocalizationProvider>
				</Box>
			</Box>
			{/* <Box sx={{mb: 4}}>
				<Typography>Status: {getStatus()}</Typography>
			</Box> */}
			<Box>
				<Typography
					sx={{
						fontFamily: "Roboto",
						fontSize: "14px",
						fontWeight: 400,
						lineHeight: "20.02px",
						letterSpacing: "0.17px",
						textAlign: "left",
						mb: "14px",
					}}
				>
					{t("workers_attachments-maximum-files")}
				</Typography>
				<FileUpload
					workerCertFiles={values.workerCertFiles}
					files={files}
					setFiles={setFiles}
					setFilesToDelete={setFilesToDelete}
					fileUploadErrors={fileUploadErrors}
					setFieldValue={setFieldValue}
				/>
			</Box>
		</Box>
	);
};

export default EditCertificationForm;
